<script setup>
import { computed, useTemplateRef } from 'vue';
import { useWindowSize, useElementBounding } from '@vueuse/core';

const props = defineProps({
  wide: { type: Boolean, default: false },
  option: {
    type: Object,
    default: () => {},
  },
  subMenuAvailable: {
    type: Boolean,
    default: true,
  },
});

const menuRef = useTemplateRef('menuRef');
const { width: windowWidth, height: windowHeight } = useWindowSize();
const { bottom, right, left, top } = useElementBounding(menuRef);

// Vertical position
const verticalPosition = computed(() => {
  const SUBMENU_HEIGHT = 240; // 15rem in pixels
  const spaceBelow = windowHeight.value - bottom.value;
  return spaceBelow < SUBMENU_HEIGHT ? 'bottom-0' : 'top-0';
});

// Horizontal position
const horizontalPosition = computed(() => {
  const SUBMENU_WIDTH = 224;
  const spaceRight = windowWidth.value - right.value;
  return spaceRight < SUBMENU_WIDTH ? 'right-full' : 'left-full';
});

const wideSubmenuStyle = computed(() => {
  if (!props.wide) return undefined;
  const width = Math.min(352, windowWidth.value - 16);
  const height = Math.min(352, windowHeight.value - 16);
  const preferredLeft =
    right.value + width <= windowWidth.value - 8
      ? right.value
      : left.value - width;
  return {
    position: 'fixed',
    width: `${width}px`,
    maxHeight: `${height}px`,
    left: `${Math.max(8, Math.min(preferredLeft, windowWidth.value - width - 8))}px`,
    top: `${Math.max(8, Math.min(top.value, windowHeight.value - height - 8))}px`,
  };
});

const submenuPosition = computed(() => [
  verticalPosition.value,
  horizontalPosition.value,
]);
</script>

<template>
  <div
    ref="menuRef"
    class="menu-with-submenu"
    :class="!subMenuAvailable ? 'opacity-50 cursor-not-allowed' : ''"
    role="menuitem"
    :tabindex="subMenuAvailable ? 0 : -1"
    aria-haspopup="menu"
  >
    <div class="flex min-w-0 items-center gap-2">
      <span class="menu-icon-shell">
        <fluent-icon :icon="option.icon" size="16" />
      </span>
      <p class="m-0 truncate text-sm font-medium leading-5">
        {{ option.label }}
      </p>
    </div>
    <fluent-icon
      icon="chevron-right"
      size="14"
      class="flex-shrink-0 text-n-slate-9"
    />
    <div
      v-if="subMenuAvailable"
      class="submenu"
      :class="wide ? 'submenu--wide' : submenuPosition"
      :style="wideSubmenuStyle"
      role="menu"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped lang="scss">
.menu-with-submenu {
  @apply relative flex min-h-9 w-full cursor-pointer items-center justify-between gap-2 rounded-lg px-2 py-1 text-n-slate-12 outline-none transition-colors;

  &:hover,
  &:focus-visible,
  &:focus-within {
    @apply bg-n-alpha-2;

    > div > .menu-icon-shell {
      @apply bg-n-solid-2 text-n-brand;
    }
  }

  &:hover > .submenu,
  &:focus-within > .submenu {
    @apply block;
  }

  &:hover > .submenu--wide,
  &:focus-within > .submenu--wide {
    @apply flex;
  }
}

.menu-icon-shell {
  @apply grid size-7 flex-shrink-0 place-items-center rounded-md bg-n-slate-3 text-n-slate-11 transition-colors;
}

.submenu {
  @apply absolute z-50 hidden max-h-[15rem] w-56 max-w-[calc(100vw-2rem)] cursor-pointer overflow-x-hidden overflow-y-auto rounded-xl border border-n-weak bg-n-solid-2 p-1.5 shadow-xl;
}

.submenu--wide {
  @apply flex-col overflow-hidden p-0 cursor-default;
}
</style>
